'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CircleHelp, Coins } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { generateGridAvatarUrl } from '@/lib/avatar';

// ─── Avatar address sets ────────────────────────────────────────

const GRID_ADDRS = [
  [
    '0x1a2b3c4d5e6f7890ab1def1234567890abcdef12',
    '0xab12ef0987654321ab2def0987654321abcdef09',
    '0x98765432fedcba1098a6543210fedcba98765432',
    '0xdeadbeefcafe1234ba3e567890abcdef12345678',
  ],
  [
    '0xfeed12ce12345678de4dbeef9876543210abcdef',
    '0x111122223333444a5556a6667777888899990000',
    '0xaaabbb1ccdddeeeff500b111222333444555666a',
    '0x777788a899990000aa6bbbcccdddeeefff000111',
  ],
  [
    '0x222233b344445555667677778888999900001111',
    '0x555566c677778888998900001111222233334444',
    '0x333344d455556666770788889999000011112222',
    '0x666677e889990001118222333344445555666777',
  ],
];

const BOARDROOM_ADDRS = [
  '0x444455f566667777889899990000111122223333',
  '0x888899a900001111220233334444555566667777',
  '0x000011b12222333344a455556666777788889999',
  '0xbbbbccd2ddddeeee1b112222333344445555aaaa',
];

// ─── Step definitions ───────────────────────────────────────────

type StepId = 0 | 1 | 2;

const STEPS: { id: StepId; heading: React.ReactNode; text: string }[] = [
  {
    id: 0,
    heading: (
      <>
        Grids generate{' '}
        <span className="bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">
          tax revenue
        </span>
      </>
    ),
    text: 'Grids on Takeover generate tax revenue from market makers who price the value of their seat.',
  },
  {
    id: 1,
    heading: (
      <>
        Revenue flows to the{' '}
        <span className="bg-gradient-to-r from-win-green to-emerald-400 bg-clip-text text-transparent">
          Boardroom
        </span>
      </>
    ),
    text: 'This tax revenue flows to the Boardroom seat holders, who are tasked with governing and pricing protocol yield.',
  },
  {
    id: 2,
    heading: (
      <>
        Excess revenue{' '}
        <span className="bg-gradient-to-r from-accent-blue to-accent-blue-light bg-clip-text text-transparent">
          grows the protocol
        </span>
      </>
    ),
    text: 'Excess tax revenue collected in TAKEOVER is earmarked for redistribution to help grow the protocol.',
  },
];

// ─── Shared scene primitives ────────────────────────────────────

function TinyGrid({
  addresses,
  pulsingCell,
  delay = 0,
}: {
  addresses: string[];
  pulsingCell: number | null;
  delay?: number;
}) {
  return (
    <motion.div
      className="grid grid-cols-2 gap-[2px] w-[52px] h-[52px] sm:w-[58px] sm:h-[58px] p-0.5 rounded-lg border border-border/50 bg-card/20"
      initial={{ opacity: 0, y: -8, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay, type: 'spring', stiffness: 300, damping: 22 }}
    >
      {addresses.map((addr, i) => (
        <div
          key={i}
          className={cn(
            'rounded-[3px] overflow-hidden relative border transition-all duration-500',
            pulsingCell === i
              ? 'border-amber-500/60 shadow-[0_0_8px_2px_rgba(245,158,11,0.3)]'
              : 'border-transparent',
          )}
        >
          {pulsingCell === i && (
            <div className="absolute inset-0 bg-amber-400/25 z-10" />
          )}
          <img src={generateGridAvatarUrl(addr)} alt="" className="w-full h-full object-cover" />
        </div>
      ))}
    </motion.div>
  );
}

function ParticleStream({
  color,
  count = 3,
  height = 50,
}: {
  color: string;
  count?: number;
  height?: number;
}) {
  return (
    <div className="relative flex justify-center" style={{ height }}>
      <div className={cn('absolute left-1/2 -translate-x-px w-px h-full opacity-10', color)} />
      {Array.from({ length: count }).map((_, i) => (
        <motion.div
          key={i}
          className={cn('absolute w-1.5 h-1.5 rounded-full', color)}
          style={{ left: 'calc(50% - 3px)' }}
          animate={{
            y: [0, height - 6],
            opacity: [0, 0.85, 0.85, 0],
            scale: [0.3, 1, 1, 0.3],
          }}
          transition={{
            duration: 1.4,
            delay: i * (1.4 / count),
            repeat: Infinity,
            ease: 'easeInOut',
            times: [0, 0.15, 0.85, 1],
          }}
        />
      ))}
    </div>
  );
}

function FlowLabel({
  icon: Icon,
  label,
  color,
  delay = 0,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  color: 'amber' | 'green' | 'blue';
  delay?: number;
}) {
  const styles = {
    amber: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
    green: 'border-win-green/30 bg-win-green/10 text-win-green',
    blue: 'border-accent-blue/30 bg-accent-blue/10 text-accent-blue-light',
  };
  return (
    <motion.div
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[11px] font-semibold',
        styles[color],
      )}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, type: 'spring', stiffness: 300, damping: 22 }}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </motion.div>
  );
}

// ─── Scene 1: Tax Generation ────────────────────────────────────
// 3 tiny grids with staggered amber pulses → particle stream → "Tax Revenue" box

function TaxGenerationScene() {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => (t + 1) % 12), 500);
    return () => clearInterval(id);
  }, []);

  const gridIdx = Math.floor(tick / 4);
  const cellIdx = tick % 4;

  return (
    <div className="flex flex-col items-center">
      <div className="flex items-center gap-2.5 sm:gap-3">
        {GRID_ADDRS.map((addrs, gi) => (
          <TinyGrid
            key={gi}
            addresses={addrs}
            pulsingCell={gridIdx === gi ? cellIdx : null}
            delay={gi * 0.1}
          />
        ))}
      </div>
      <ParticleStream color="bg-amber-400" height={56} count={3} />
      <FlowLabel icon={Coins} label="Tax Revenue" color="amber" delay={0.3} />
    </div>
  );
}

// ─── Scene 2: Revenue Flow ──────────────────────────────────────
// "Tax Revenue" box → green particle stream → 4 boardroom holder avatars

function RevenueFlowScene() {
  const [glowIdx, setGlowIdx] = useState(-1);

  useEffect(() => {
    let idx = -1;
    const id = setInterval(() => {
      idx = idx >= 3 ? -1 : idx + 1;
      setGlowIdx(idx);
    }, 700);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="flex flex-col items-center">
      <FlowLabel icon={Coins} label="Tax Revenue" color="amber" />
      <ParticleStream color="bg-emerald-400" height={48} count={3} />
      <motion.div
        className="flex items-center gap-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        {BOARDROOM_ADDRS.map((addr, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 + i * 0.1, type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div
              className={cn(
                'w-10 h-10 sm:w-11 sm:h-11 rounded-full overflow-hidden border-2 transition-all duration-500',
                glowIdx === i
                  ? 'border-win-green shadow-[0_0_12px_3px_rgba(34,197,94,0.35)]'
                  : 'border-border/50',
              )}
            >
              <img src={generateGridAvatarUrl(addr)} alt="" className="w-full h-full object-cover" />
            </div>
          </motion.div>
        ))}
      </motion.div>
      <motion.p
        className="text-[10px] text-muted-foreground mt-2 font-medium"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
      >
        Boardroom Holders
      </motion.p>
    </div>
  );
}

// ─── Scene 3: Redistribution ────────────────────────────────────
// Central mascot with coins radiating outward

function PulseRing({ delay = 0 }: { delay?: number }) {
  return (
    <motion.div
      className="absolute inset-0 rounded-full border-2 border-brand-red/20"
      animate={{ scale: [1, 2.5], opacity: [0.5, 0] }}
      transition={{ duration: 2.5, delay, repeat: Infinity, ease: 'easeOut' }}
    />
  );
}

function RadialCoin({ angle, delay }: { angle: number; delay: number }) {
  const rad = (angle * Math.PI) / 180;
  const endX = Math.cos(rad) * 70;
  const endY = Math.sin(rad) * 70;

  return (
    <motion.div
      className="absolute"
      style={{ left: 'calc(50% - 8px)', top: 'calc(50% - 8px)' }}
      animate={{
        x: [0, endX],
        y: [0, endY],
        opacity: [0, 1, 0.8, 0],
        scale: [0.3, 1, 0.9, 0.4],
      }}
      transition={{
        duration: 1.8,
        delay,
        repeat: Infinity,
        repeatDelay: 0.6,
        ease: 'easeOut',
        times: [0, 0.25, 0.7, 1],
      }}
    >
      <Coins className="w-4 h-4 text-accent-blue-light" />
    </motion.div>
  );
}

function RedistributionScene() {
  const angles = [0, 45, 90, 135, 180, 225, 270, 315];

  return (
    <div className="relative w-[220px] h-[180px] mx-auto flex items-center justify-center">
      {/* Central boardroom portrait */}
      <motion.div
        className="relative"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 18 }}
      >
        <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-brand-red/10 border-2 border-brand-red/30 flex items-center justify-center overflow-hidden shadow-[0_0_20px_4px_rgba(255,61,61,0.1)]">
          <img
            src="/boardroom.png"
            alt=""
            className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-full"
            style={{ imageRendering: 'pixelated' }}
          />
        </div>
        <PulseRing delay={0} />
        <PulseRing delay={1.2} />
      </motion.div>

      {/* Coins radiating outward in all directions */}
      {angles.map((angle, i) => (
        <RadialCoin key={angle} angle={angle} delay={i * 0.3} />
      ))}
    </div>
  );
}

// ─── Scene wrapper for AnimatePresence ──────────────────────────

const sceneTransition = {
  initial: { opacity: 0, y: 12, scale: 0.97 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -10, scale: 0.97 },
  transition: { duration: 0.3 },
};

function SceneForStep({ step }: { step: StepId }) {
  switch (step) {
    case 0:
      return <TaxGenerationScene />;
    case 1:
      return <RevenueFlowScene />;
    case 2:
      return <RedistributionScene />;
  }
}

// ─── Modal ──────────────────────────────────────────────────────

export function BoardroomExplainerModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [step, setStep] = useState<StepId>(0);

  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  const currentStep = STEPS[step];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden [&>button]:hidden">
        <DialogTitle className="sr-only">What is the Boardroom?</DialogTitle>

        {/* Animation area — fixed height prevents layout shift */}
        <div className="h-[220px] flex items-center justify-center px-6 pt-6 pb-2">
          <AnimatePresence mode="wait">
            <motion.div key={step} {...sceneTransition}>
              <SceneForStep step={step} />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Text area */}
        <div className="px-6 pb-2">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
            >
              <h3 className="text-lg font-bold tracking-tight mb-1.5">
                {currentStep.heading}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {currentStep.text}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between px-6 pb-5 pt-3">
          <div className="flex gap-1.5">
            {STEPS.map((s) => (
              <button
                key={s.id}
                onClick={() => setStep(s.id)}
                className={cn(
                  'h-1.5 rounded-full transition-all duration-300 cursor-pointer hover:opacity-80',
                  s.id === step
                    ? cn(
                        'w-5',
                        step === 0
                          ? 'bg-amber-500'
                          : step === 1
                            ? 'bg-win-green'
                            : 'bg-accent-blue',
                      )
                    : 'w-1.5 bg-muted-foreground/25 hover:bg-muted-foreground/40',
                )}
              />
            ))}
          </div>
          <div className="flex gap-2">
            {step < 2 ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setStep((step + 1) as StepId)}
                className="text-xs"
              >
                Next
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => onOpenChange(false)}
                className="text-xs"
              >
                Got it
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Trigger button ─────────────────────────────────────────────

export function BoardroomExplainerButton() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={cn(
          'inline-flex items-center justify-center rounded-full',
          'w-7 h-7 border border-border/50 bg-muted/30',
          'text-muted-foreground hover:text-foreground hover:bg-muted/60',
          'transition-colors duration-200',
        )}
        aria-label="What is the Boardroom?"
      >
        <CircleHelp className="h-3.5 w-3.5" />
      </button>
      <BoardroomExplainerModal open={open} onOpenChange={setOpen} />
    </>
  );
}
